import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isSubscriptionUiActive } from '../../utils/subscriptionUiActive';
import { Card, Typography, Button, Space, Alert, Spin } from 'antd';
import { CrownOutlined, LockOutlined } from '@ant-design/icons';

const { Title } = Typography;

interface SubscriptionProtectedRouteProps {
  children: React.ReactNode;
  allowUnsubscribed?: boolean; // Eğer true ise, abonelik olmasa bile erişim izni ver (örn: profile, subscription sayfaları)
}

const SubscriptionProtectedRoute: React.FC<SubscriptionProtectedRouteProps> = ({ 
  children, 
  allowUnsubscribed = false
}) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Yükleme durumu
  if (isLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Kullanıcı yoksa login'e yönlendir
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Eğer allowUnsubscribed true ise, abonelik kontrolü yapma
  if (allowUnsubscribed) {
    return <>{children}</>;
  }

  const isSubscriptionActive = isSubscriptionUiActive(user);

  // Aktif abonelik yoksa uyarı göster
  if (!isSubscriptionActive) {
    const handleSubscribeClick = () => {
      // Panel içindeysek, subscription tab'ını açmak için URL'e query parameter ekle
      const currentPath = location.pathname;
      if (currentPath.includes('/profile') || currentPath.includes('/mentee-profile') || currentPath.includes('/company-panel')) {
        // Aynı sayfada kal, sadece tab değiştir
        navigate(`${currentPath}?tab=subscription`);
      } else {
        // Panel sayfasına git
        const panelPath = user?.role === 'Mentor' ? '/profile' : 
                         user?.role === 'Mentee' ? '/mentee-profile' : 
                         user?.role === 'Company' ? '/company-panel' : '/login';
        navigate(`${panelPath}?tab=subscription`);
      }
    };

    const handleProfileClick = () => {
      const panelPath = user?.role === 'Mentor' ? '/profile' : 
                       user?.role === 'Mentee' ? '/mentee-profile' : 
                       user?.role === 'Company' ? '/company-panel' : '/login';
      navigate(panelPath);
    };

    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-tertiary)',
        padding: '24px'
      }}>
        <Card style={{ maxWidth: '600px', textAlign: 'center' }}>
          <LockOutlined style={{ fontSize: '64px', color: 'var(--error)', marginBottom: '24px' }} />
          <Title level={2}>Abonelik Gerekli</Title>
          <Alert
            message="Bu sayfaya erişmek için aktif aboneliğiniz olmalıdır."
            description="Panel sayfalarına erişmek için aylık veya yıllık abonelik satın alabilirsiniz."
            type="warning"
            showIcon
            style={{ marginBottom: '24px', textAlign: 'left' }}
          />
          <Space>
            <Button 
              type="primary" 
              size="large"
              icon={<CrownOutlined />}
              onClick={handleSubscribeClick}
            >
              Abonelik Satın Al
            </Button>
            <Button 
              size="large"
              onClick={handleProfileClick}
            >
              Profil Sayfasına Git
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default SubscriptionProtectedRoute;

